import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Cat, Check, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { BREEDS, CAT_AVATARS } from '../../mock';
import api from '../../api';

const AVATAR_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/avatars/`;

export default function AddCat() {
  const nav = useNavigate();
  const { addCat } = useApp();
  const fileRef = useRef(null);
  const [name, setName] = useState('');
  const [breed, setBreed] = useState(BREEDS[0]);
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [avatar, setAvatar] = useState(CAT_AVATARS[1] || CAT_AVATARS[0]);
  const [uploaded, setUploaded] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const r = await api.post('/cats/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = AVATAR_BASE + r.data.path;
      setUploaded(url);
      setAvatar(url);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not upload photo'); }
    finally { setUploadBusy(false); }
  };

  const save = async () => {
    if (!name) { toast.error("Please add your cat's name"); return; }
    setBusy(true);
    try {
      await addCat({ name, breed, age, avatar, weight, weight_unit: weightUnit });
      toast.success(`${name} added!`);
      nav('/app/profile');
    } catch (e) { toast.error('Could not add cat'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6">
      <button onClick={() => nav('/app/profile')} className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div className="mx-auto max-w-md animate-fade-up">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent"><Cat className="h-7 w-7 text-[hsl(var(--coral))]" /></div>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight">Add another cat.</h1>
        <p className="mt-2 text-muted-foreground">Keep every cat's care in one place.</p>

        <div className="mt-6">
          <Label className="text-sm font-semibold">Choose a photo</Label>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="add-cat-upload-input" />
          <div className="mt-2 flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {uploaded && (
              <button onClick={() => setAvatar(uploaded)} className="relative shrink-0">
                <img src={uploaded} alt="your cat" className={`h-16 w-16 rounded-2xl object-cover transition ${avatar === uploaded ? 'ring-4 ring-[hsl(var(--coral))]' : 'ring-1 ring-border opacity-80'}`} />
                {avatar === uploaded && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--coral))]"><Check className="h-3 w-3 text-white" /></span>}
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={uploadBusy} data-testid="add-cat-upload-btn" className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-border bg-secondary/40 text-muted-foreground hover:border-[hsl(var(--coral))]">
              {uploadBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[10px] font-bold">Upload</span></>}
            </button>
            {CAT_AVATARS.map((a) => (
              <button key={a} onClick={() => setAvatar(a)} className="relative shrink-0">
                <img src={a} alt="cat" className={`h-16 w-16 rounded-2xl object-cover transition ${avatar === a ? 'ring-4 ring-[hsl(var(--coral))]' : 'ring-1 ring-border opacity-80'}`} />
                {avatar === a && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--coral))]"><Check className="h-3 w-3 text-white" /></span>}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-sm font-semibold">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Simba" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Breed</Label>
            <select value={breed} onChange={(e) => setBreed(e.target.value)} className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
              {BREEDS.map((b) => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Age (years)</Label>
            <Input value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g. 2" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Weight <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={weight} onChange={(e) => setWeight(e.target.value)} inputMode="decimal" placeholder="e.g. 10" data-testid="cat-weight" className="h-12 flex-1 rounded-xl bg-white" />
              <select value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} data-testid="cat-weight-unit" className="h-12 w-24 rounded-xl border border-input bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                <option value="lbs">lbs</option>
                <option value="kg">kg</option>
              </select>
            </div>
          </div>
        </div>

        <button onClick={save} disabled={busy} className="coral-btn mt-8 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}Add cat
        </button>
      </div>
    </div>
  );
}
